import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarSync, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LABELS = {
  'appartement-1': 'Apt Terrasse',
  'appartement-2': 'Apt Panorama',
  'gite-seul': 'Gite seul',
};

function feedAge(isoStr) {
  if (!isoStr) return null;
  const mins = Math.round((Date.now() - new Date(isoStr).getTime()) / 60000);
  if (mins < 60) return `il y a ${mins} min`;
  if (mins < 1440) return `il y a ${Math.round(mins / 60)} h`;
  return `il y a ${Math.round(mins / 1440)} j`;
}

export const IcalHealthWidget = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    axios.get(`${BACKEND_URL}/api/admin/ical-config`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setConfig(r.data))
      .catch(() => {});
  }, []);

  if (!config) return null;
  const accs = Object.keys(LABELS).map(key => ({
    key,
    label: LABELS[key],
    feeds: config.accommodations?.[key]?.import_urls || [],
  }));
  const hasIssue = accs.some(a => a.feeds.some(f => f.last_sync_status?.startsWith('error')));

  return (
    <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="ical-health-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarSync className="w-5 h-5 text-[#8A9A5B]" strokeWidth={1.5} />
          <h3 className="font-semibold text-[#2C3E30]">Sante des synchronisations</h3>
          <span
            data-testid="ical-health-global-dot"
            className={`w-2.5 h-2.5 rounded-full ${hasIssue ? 'bg-red-500 animate-pulse' : 'bg-emerald-500'}`}
          />
        </div>
        <button
          data-testid="ical-health-manage-link"
          onClick={() => navigate('/admin/ical')}
          className="text-xs text-[#8A9A5B] hover:text-[#2C3E30] flex items-center gap-0.5"
        >
          Gerer <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="space-y-2">
        {accs.map(acc => (
          <div key={acc.key} className="flex items-center gap-3 flex-wrap" data-testid={`ical-health-row-${acc.key}`}>
            <span className="w-[110px] shrink-0 text-sm text-[#2C3E30]">{acc.label}</span>
            {acc.feeds.length === 0 && (
              <span className="text-xs text-[#aaa] italic">Aucun flux</span>
            )}
            {acc.feeds.map((f, i) => {
              const isError = f.last_sync_status?.startsWith('error');
              const never = !f.last_sync;
              return (
                <span
                  key={f.id || i}
                  data-testid={`ical-health-feed-${acc.key}-${i}`}
                  title={isError ? f.last_sync_status : never ? 'Jamais synchronise' : `${f.events_count || 0} evt(s) — ${feedAge(f.last_sync)}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border ${
                    isError
                      ? 'border-red-200 bg-red-50 text-red-700'
                      : never
                        ? 'border-[#E5E5E0] bg-[#F5F5F0] text-[#888]'
                        : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${isError ? 'bg-red-500' : never ? 'bg-gray-400' : 'bg-emerald-500'}`} />
                  {f.platform || 'Flux'}
                  {!isError && !never && <span className="opacity-70">· {feedAge(f.last_sync)}</span>}
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};
